/**
 * Penampang cluster: di mana, di tiap sumur, batuan yang dicluster di crossplot.
 *
 * Crossplot menjawab *batuan macam apa* dan kehilangan kedalamannya di situ;
 * penampang ini mengembalikannya. Tiap sumur lingkup menjadi satu kolom pada
 * sumbu MD-nya sendiri, dengan kurva X dan Y crossplot dan satu jalur cluster
 * yang diwarnai menurut kelas yang memuat tiap sampel — kelas tersimpan pada
 * sepasang sumbu ini, dan bentuk yang sedang digambar sebagai "Seleksi".
 *
 * Penggolongannya dikerjakan server pada resolusi penuh dengan
 * `crossplot.classify`, yang sama dengan penerbitan kurva kelas. Awan di layar
 * sudah dijarangkan menjadi enam ribu titik, dan penampang dari titik yang
 * dijarangkan akan memajang selang berlubang yang tidak ada di sumurnya.
 */

import { useMemo, useState } from "react";

import { api } from "../api";
import { useAsync } from "../hooks";
import { coordinateStep, coordinateTicks, tickDecimals } from "../lib/ticks";
import type { ClusterSection as Payload, ClusterSectionWell, Well } from "../types";
import { Chip, Empty, Loading, Notice } from "./ui";

/** Bentuk yang sedang digambar diarsir, supaya tidak tertukar dengan kelas tersimpan
 *  yang kebetulan berwarna aksen yang sama. */
const SELECTION = "url(#cluster-selection)";
const SELECTION_SWATCH = "repeating-linear-gradient(135deg, var(--accent) 0 3px, transparent 3px 6px)";
const TRACK = 64;
const CODE_TRACK = 26;
const AXIS = 34;
const GAP = 4;
const HEIGHT = 560;
const TOP = 6;
/** Kolom sumur yang digambar per halaman. */
const PAGE = 24;

type Range = { lo: number; hi: number; log: boolean };

function place(range: Range, v: number): number {
  const a = range.log ? Math.log10(range.lo) : range.lo;
  const b = range.log ? Math.log10(range.hi) : range.hi;
  const t = range.log ? Math.log10(v) : v;
  return Math.min(1, Math.max(0, (t - a) / (b - a || 1)));
}

/** Rentang bersama satu kurva di seluruh sumur, supaya kolomnya bisa dibandingkan. */
function sharedRange(
  wells: ClusterSectionWell[],
  key: "x" | "y",
  scale: [number, number] | null,
  log: boolean,
): Range {
  if (scale && (!log || (scale[0] > 0 && scale[1] > 0))) {
    return { lo: Math.min(scale[0], scale[1]), hi: Math.max(scale[0], scale[1]), log };
  }
  const values = wells
    .flatMap((w) => w[key])
    .filter((v): v is number => v !== null && Number.isFinite(v) && (!log || v > 0))
    .sort((a, b) => a - b);
  if (!values.length) return { lo: log ? 0.1 : 0, hi: 1, log };
  const lo = values[Math.floor(values.length * 0.01)];
  const hi = values[Math.ceil(values.length * 0.99) - 1];
  return lo < hi ? { lo, hi, log } : { lo: lo - 1, hi: hi + 1, log };
}

/** Potongan berurutan dengan kode yang sama; kedalaman yang melompat memutusnya. */
function runs(well: ClusterSectionWell): { code: number; top: number; base: number }[] {
  const out: { code: number; top: number; base: number }[] = [];
  const half = (well.step ?? 0.15) / 2;
  const jump = (well.step ?? 0.15) * 4 * Math.max(1, well.samples / Math.max(1, well.md.length));
  well.code.forEach((code, i) => {
    const md = well.md[i];
    const last = out[out.length - 1];
    if (code === null) return;
    if (last && last.code === code && md - last.base <= jump) last.base = md + half;
    else out.push({ code, top: md - half, base: md + half });
  });
  return out;
}

function curvePath(
  mds: number[],
  values: (number | null)[],
  range: Range,
  y: (md: number) => number,
  x0: number,
  width: number,
  jump: number,
): string {
  let d = "";
  let pen = false;
  let prev: number | null = null;
  values.forEach((v, i) => {
    const md = mds[i];
    const ok = v !== null && Number.isFinite(v) && (!range.log || v > 0);
    if (!ok || (prev !== null && md - prev > jump)) pen = false;
    if (ok) {
      const px = x0 + place(range, v as number) * width;
      d += `${pen ? "L" : "M"}${px.toFixed(1)},${y(md).toFixed(1)}`;
      pen = true;
    }
    prev = md;
  });
  return d;
}

export function ClusterSectionView({
  projectId,
  params,
  wells,
}: {
  projectId: number;
  /** Parameter kueri: kurva, ruang log, lingkup crossplot, dan bentuk yang digambar. */
  params: Record<string, string | undefined>;
  /** Sumur proyek, untuk garis marker. */
  wells: Well[];
}) {
  const [onlyHit, setOnlyHit] = useState(false);
  const [showMarkers, setShowMarkers] = useState(true);
  // Satu lapangan bisa ratusan sumur; kolomnya digambar bertahap.
  const [limit, setLimit] = useState(PAGE);
  const data = useAsync(
    (signal) => api.get<Payload>(`/api/projects/${projectId}/crossplot-section/`, params, signal),
    [projectId, JSON.stringify(params)],
  );

  const payload = data.data;
  const colorOf = useMemo(() => {
    const map = new Map<number, string>();
    payload?.classes.forEach((c) => map.set(c.code, c.color || SELECTION));
    return (code: number) => map.get(code) ?? "var(--ink-5)";
  }, [payload]);

  const totals = useMemo(() => {
    const out = new Map<number, number>();
    payload?.wells.forEach((w) =>
      Object.entries(w.counts).forEach(([code, n]) => out.set(Number(code), (out.get(Number(code)) ?? 0) + n)),
    );
    return out;
  }, [payload]);

  if (data.loading && !payload) return <Loading />;
  if (data.error) return <Notice tone="error">{data.error}</Notice>;
  if (!payload) return null;

  const clustered = (w: ClusterSectionWell) =>
    Object.values(w.counts).reduce((a, b) => a + b, 0) / Math.max(1, w.samples);
  const eligible = payload.wells.filter((w) => !onlyHit || Object.keys(w.counts).length > 0);
  // Lebih dari satu halaman: yang paling banyak tergolong didahulukan, sebab
  // itulah yang dicari orang yang membuka penampang dari sebuah cluster.
  const ordered = eligible.length > PAGE
    ? [...eligible].sort((a, b) => clustered(b) - clustered(a))
    : eligible;
  const shown = ordered.slice(0, limit);
  const xr = sharedRange(payload.wells, "x", payload.x_scale, payload.log_x);
  const yr = sharedRange(payload.wells, "y", payload.y_scale, payload.log_y);
  const columnWidth = AXIS + TRACK * 2 + CODE_TRACK + GAP * 3;
  const byId = new Map(wells.map((w) => [w.id, w]));
  const allSamples = payload.wells.reduce((n, w) => n + w.samples, 0);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10, minHeight: 0 }}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center" }}>
        {payload.classes.map((c) => {
          const n = totals.get(c.code) ?? 0;
          return (
            <span key={c.code} style={{ display: "inline-flex", gap: 6, alignItems: "center",
                                         font: "400 10.5px var(--font-sans)", color: "var(--ink-3)" }}>
              <span style={{ width: 11, height: 11, borderRadius: 2, background: c.color || SELECTION_SWATCH,
                             border: c.color ? undefined : "1px solid var(--accent)" }} />
              {c.name}
              <span style={{ font: "400 9.5px var(--font-mono)", color: "var(--ink-6)" }}>
                {n.toLocaleString("en-US")} sampel
                {allSamples ? ` · ${((n / allSamples) * 100).toFixed(1)}%` : ""}
              </span>
            </span>
          );
        })}
        <span style={{ flex: 1 }} />
        <Chip active={onlyHit} onClick={() => setOnlyHit(!onlyHit)}>Hanya sumur bercluster</Chip>
        <Chip active={showMarkers} onClick={() => setShowMarkers(!showMarkers)}>Marker</Chip>
      </div>
      {!payload.classes.length && (
        <Notice tone="warn">
          Belum ada cluster pada {payload.x_label}–{payload.y_label}: gambar bentuk di crossplot atau
          simpan kelas lebih dulu.
        </Notice>
      )}
      <span style={{ font: "400 9.5px var(--font-mono)", color: "var(--ink-6)" }}>
        {payload.x_label} {payload.log_x ? "(log) " : ""}
        {xr.lo.toPrecision(3)}–{xr.hi.toPrecision(3)} · {payload.y_label} {payload.log_y ? "(log) " : ""}
        {yr.lo.toPrecision(3)}–{yr.hi.toPrecision(3)} · skala sama di semua sumur · MD tiap sumur sendiri
      </span>
      {shown.length === 0 ? (
        <Empty>Tidak ada sumur dengan sampel di lingkup ini.</Empty>
      ) : (
        <div className="scroll-x" style={{ display: "flex", gap: 14, overflowX: "auto", paddingBottom: 6 }}>
          {shown.map((well) => {
            const lo = well.md[0];
            const hi = well.md[well.md.length - 1];
            const span = Math.max(hi - lo, 1);
            const y = (md: number) => TOP + ((md - lo) / span) * (HEIGHT - TOP * 2);
            const step = coordinateStep(span, 8);
            const ticks = coordinateTicks(lo, hi, step);
            const jump = (well.step ?? 0.15) * 4 * Math.max(1, well.samples / Math.max(1, well.md.length));
            const xs = AXIS + GAP;
            const ys = xs + TRACK + GAP;
            const cs = ys + TRACK + GAP;
            const markers = showMarkers
              ? (byId.get(well.id)?.markers ?? []).filter((m) => m.md >= lo && m.md <= hi)
              : [];
            return (
              <div key={well.id} style={{ flex: "none", width: columnWidth, display: "flex",
                                          flexDirection: "column", gap: 4 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ width: 8, height: 8, borderRadius: 4, background: well.color }} />
                  <span style={{ font: "600 11px var(--font-sans)", color: "var(--ink)" }}>{well.name}</span>
                </div>
                <div style={{ display: "flex", gap: GAP, paddingLeft: xs,
                              font: "400 9px var(--font-mono)", color: "var(--ink-5)" }}>
                  <span style={{ width: TRACK }}>{payload.x_label}</span>
                  <span style={{ width: TRACK }}>{payload.y_label}</span>
                  <span style={{ width: CODE_TRACK }}>CL</span>
                </div>
                <svg width={columnWidth} height={HEIGHT} role="img"
                     aria-label={`Penampang cluster ${well.name}`}
                     style={{ background: "var(--canvas-deep)", border: "1px solid var(--line-strong)",
                              borderRadius: "var(--radius)" }}>
                  <defs>
                    <pattern id="cluster-selection" width="6" height="6" patternUnits="userSpaceOnUse"
                             patternTransform="rotate(45)">
                      <rect width="6" height="6" fill="var(--accent-wash)" />
                      <rect width="3" height="6" fill="var(--accent)" />
                    </pattern>
                  </defs>
                  {ticks.map((t) => (
                    <g key={t}>
                      <line x1={AXIS} x2={columnWidth} y1={y(t)} y2={y(t)}
                            stroke="var(--grid-line)" strokeWidth={1} />
                      <text x={AXIS - 3} y={y(t) + 3} textAnchor="end"
                            style={{ font: "400 8.5px var(--font-mono)", fill: "var(--ink-5)" }}>
                        {t.toFixed(tickDecimals(step))}
                      </text>
                    </g>
                  ))}
                  {[xs, ys, cs].map((x0, i) => (
                    <rect key={i} x={x0} y={TOP} width={i === 2 ? CODE_TRACK : TRACK}
                          height={HEIGHT - TOP * 2} fill="none" stroke="var(--line)" />
                  ))}
                  {runs(well).map((r, i) => (
                    <rect key={i} x={cs} y={y(r.top)} width={CODE_TRACK}
                          height={Math.max(1, y(r.base) - y(r.top))} fill={colorOf(r.code)}>
                      <title>
                        {payload.classes.find((c) => c.code === r.code)?.name ?? r.code} ·{" "}
                        {r.top.toFixed(1)}–{r.base.toFixed(1)} m
                      </title>
                    </rect>
                  ))}
                  {/* Kurva diberi latar pita cluster tipis supaya selang yang
                      tergolong terbaca sejajar dengan bentuk kurvanya. */}
                  {runs(well).map((r, i) => (
                    <rect key={`b${i}`} x={xs} y={y(r.top)} width={TRACK * 2 + GAP}
                          height={Math.max(1, y(r.base) - y(r.top))} fill={colorOf(r.code)}
                          fillOpacity={0.14} />
                  ))}
                  <path d={curvePath(well.md, well.x, xr, y, xs, TRACK, jump)} fill="none"
                        stroke="var(--ink-2)" strokeWidth={1} />
                  <path d={curvePath(well.md, well.y, yr, y, ys, TRACK, jump)} fill="none"
                        stroke="var(--accent-bright)" strokeWidth={1} />
                  {markers.map((m) => (
                    <g key={m.id}>
                      <line x1={AXIS} x2={columnWidth} y1={y(m.md)} y2={y(m.md)}
                            stroke={m.color || "var(--ink-4)"} strokeWidth={1} strokeDasharray="3 2" />
                      <text x={columnWidth - 2} y={y(m.md) - 2} textAnchor="end"
                            style={{ font: "500 8px var(--font-sans)", fill: m.color || "var(--ink-4)" }}>
                        {m.name}
                      </text>
                    </g>
                  ))}
                </svg>
                <span style={{ font: "400 9px var(--font-mono)", color: "var(--ink-6)" }}>
                  {lo.toFixed(0)}–{hi.toFixed(0)} m · {well.samples.toLocaleString("en-US")} sampel
                  {Object.keys(well.counts).length
                    ? ` · ${Object.values(well.counts).reduce((a, b) => a + b, 0).toLocaleString("en-US")} bercluster`
                    : " · tanpa cluster"}
                </span>
              </div>
            );
          })}
        </div>
      )}
      {ordered.length > shown.length && (
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <Chip active={false} onClick={() => setLimit(limit + PAGE)}>
            Tampilkan {Math.min(PAGE, ordered.length - shown.length)} sumur berikutnya
          </Chip>
          <span style={{ font: "400 9.5px var(--font-mono)", color: "var(--ink-6)" }}>
            {shown.length} dari {ordered.length} sumur, diurutkan menurut bagian yang tergolong
          </span>
        </div>
      )}
      {payload.skipped.length > 0 && (
        <span style={{ font: "400 9.5px var(--font-mono)", color: "var(--ink-6)" }}>
          Dilewati: {payload.skipped.map((s) => `${s.well} (${s.reason})`).join("; ")}
        </span>
      )}
    </div>
  );
}
